import { Injectable, NgZone, Renderer2, RendererFactory2 } from "@angular/core";
import { HttpClient } from "@angular/common/http";

import { ChartsService } from "../charts/charts.service";
import {
  HsEventBusService,
  HsUtilsService,
  HsQueryBaseService,
  HsLayoutService,
  HsLanguageService,
  HsToastService,
  HsMapService,
} from "hslayers-ng";
import { Observable, of, Subject } from "rxjs";
import { catchError, map } from "rxjs/operators";
import { NgbDate, NgbDateStruct } from "@ng-bootstrap/ng-bootstrap";

import * as olExtent from "ol/extent";

import { chartDefinitionObject } from "../charts/charts-definition.interface";
import selectedDataPairs from "./data/chart-definitions.json";
import { StrategyDataParserService } from "./strategy-data-parser.service";

@Injectable({
  providedIn: "root",
})
export class StrategyCommonService {
  renderer: Renderer2;

  fetchData$ = new Subject();
  renderCharts$ = new Subject();

  climaDataLoading = false;
  baseChartsLoading = false;
  uncertainity = false;

  overview: string = "place";
  dataFrequency = "week";
  selectedDate = {
    upper: 0,
    lower: 0,
  };
  selectedCoords: Array<number>;

  apiUrl = "api-agroclimatic.lesprojekt.cz";

  selectedDataPairs;

  crops = [
    "sunflower",
    "wheat",
    "corn",
    "barley",
    "oat",
    "canolaNapus",
    "canolaRapa",
    "pea",
    "lentil",
  ];

  selectedCrop = this.crops[2];
  selectedLocation: "czechia" | "slovakia" | "latvia" = "czechia";
  dataBbox = [
    {
      code: "CZ",
      location: "czechia",
      bbox: [
        [12.1, 48.1],
        [19, 52],
      ],
      crs: "EPSG:5514",
      seasonStart: "5-1",
    },
    {
      code: "SK",
      location: "slovakia",
      bbox: [
        [17, 48.1],
        [22.9, 50.0],
      ],
      crs: "EPSG:5514",
      seasonStart: "5-1",
    },
    {
      code: "LOT",
      location: "latvia",
      bbox: [
        [21.0, 56.1],
        [28.9, 58.0],
      ],
      crs: "EPSG:5514",
      seasonStart: "5-1",
    },
    {
      code: "SJ",
      location: "sanJuan",
      bbox: [
        [-68.1, -31.2],
        [-69.0, -32.1],
      ],
      crs: "EPSG:3857",
      seasonStart: "11-1",
    },
  ];

  calendarMin: NgbDateStruct;
  calendarMax: NgbDateStruct;
  seasonStartDate: NgbDateStruct;
  seasonEndDate: NgbDateStruct;
  seasonEndWeek: number;
  frostFreePeriod = {};

  constructor(
    rendererFactory: RendererFactory2,
    public HsMapService: HsMapService,
    public HsEventBusService: HsEventBusService,
    public HsUtilsService: HsUtilsService,
    public ChartsService: ChartsService,
    public HsQueryBaseService: HsQueryBaseService,
    public HsLayoutService: HsLayoutService,
    public HsLanguageService: HsLanguageService,
    public hsToastService: HsToastService,
    public parserService: StrategyDataParserService,
    public http: HttpClient,
    private zone: NgZone
  ) {
    this.renderer = rendererFactory.createRenderer(null, null);
    this.setSeasonStart();
    this.selectedDataPairs = { ...selectedDataPairs };

    //If chart has alternatives store visible factor index
    for (const [overview, overviewObject] of Object.entries(
      this.selectedDataPairs
    )) {
      for (const [dataFrequency, frequencyObject] of Object.entries(
        overviewObject
      )) {
        for (const [chart, chartObject] of Object.entries(
          frequencyObject as chartDefinitionObject
        )) {
          if (Array.isArray(chartObject.data) && !chartObject.layered) {
            chartObject.index = 0;
            chartObject.maxIndex = chartObject.data.length - 1;
          }
        }
      }
    }

    this.HsEventBusService.mapClicked.subscribe((data) => {
      if (!this.ChartsService.generatingCharts) {
        if (this.HsLayoutService.mainpanel != "strategy") {
          this.HsLayoutService.setMainPanel("strategy");
        }
        this.seasonStartDate = new NgbDate(
          this.selectedDate.upper,
          this.seasonStartDate.month,
          this.seasonStartDate.day
        );
        this.fetchAndRender(data.coordinates.epsg4326Coordinate);
      }
    });

    this.ChartsService.chartsGenerated.subscribe((response) => {
      this.zone.run(() => {
        this.generateArrowButtons(response);
      });
    });
  }

  /**
   * Set season start based on location selected
   */
  setSeasonStart() {
    const locationSeasonStart = this.dataBbox
      .find((item) => item.location == this.selectedLocation)
      .seasonStart.split("-");
    this.seasonStartDate = new NgbDate(
      this.selectedDate.upper,
      parseInt(locationSeasonStart[0]),
      parseInt(locationSeasonStart[1])
    );
  }

  createDateRangeObject(dataFrequency, startDate, endDate) {
    let dateRange = [];
    let i = 0;
    let x = 1;
    //DONT START WITH WEEK NR 1 BUT RATHER WEEK NUMBER CALCULATED FROM StrategyCommonService.seasonStartDate
    const seasonStart: number = this.parserService.getSeasonStartWeekNumber(
      this.seasonStartDate
    );

    if (dataFrequency == "week") {
      for (i; i < 53; i++) {
        if (seasonStart + i <= 52) {
          dateRange[i] = { week: seasonStart + i, year: startDate };
        } else {
          dateRange[i] = {
            week: x,
            year: startDate + 1,
          };
          x++;
        }
      }
    } else if (dataFrequency == "month") {
      for (i; i < 12; i++) {
        if (this.seasonStartDate.month + i > 12) {
          dateRange[i] = { month: x, year: startDate + 1 };
          x++;
        } else {
          dateRange[i] = {
            month: this.seasonStartDate.month + i,
            year: startDate,
          };
        }
      }
    } else {
      while (startDate + i <= endDate) {
        dateRange[i] = { year: startDate + i };
        i++;
      }
    }
    return dateRange;
  }

  /**
   * Creates and embeds html buttons controling chart alternatives into vega SVG
   */
  generateArrowButtons(response) {
    let res, data;
    ({ res, data } = response);
    //Filter out svg groups containing charts
    const charts = [
      ...(res.view.container().querySelectorAll("g") as any),
    ].filter((g) => {
      return [...g.classList].some((c) =>
        new RegExp("agroInfoChart_(?<=_)(.*)(?=_)_group", "g").test(c)
      );
    });
    const width =
      charts[charts.length - 1].getClientRects()[0].width -
      (this.dataFrequency == "week" ? 100 : 25);
    const chevronLeft = this.createChevronButton(false, width);
    const chevronRight = this.createChevronButton(true, width);
    for (let chart of charts) {
      const factor = [...chart.classList]
        .filter((c) => c.includes("agroInfoChart_"))[0]
        .split("_")[1];
      //Search for definitionObject inside view definition object
      const definitionObject = data.definitions.charts[
        data.dataFrequency
      ].filter((chart) => chart.data.includes(factor))[0];

      if (!definitionObject?.layered && "index" in definitionObject) {
        //Clone created nodes in order to assign event listener to each of them individually
        let clonedRightNode = chevronRight.cloneNode(true);
        let clonedLeftNode = chevronLeft.cloneNode(true);
        /**
         * Add arrow event listener
         */
        this.renderer.listen(clonedRightNode, "click", (event) => {
          this.arrowClickEventHandler("right", factor);
        });
        this.renderer.listen(clonedLeftNode, "click", (event) => {
          this.arrowClickEventHandler("left", factor);
        });

        if (definitionObject.index < definitionObject.maxIndex) {
          this.renderer.appendChild(chart.children[0], clonedRightNode);
        }

        if (definitionObject.index > 0) {
          this.renderer.appendChild(chart.children[0], clonedLeftNode);
        }
      }
    }
  }

  arrowClickEventHandler(side, factor) {
    //Search for definitionObject inside source/reference object
    const factorDefinition = this.selectedDataPairs[this.overview][
      this.dataFrequency
    ].filter((fd) => fd.data.includes(factor))[0];
    factorDefinition.index =
      side == "right" ? factorDefinition.index + 1 : factorDefinition.index - 1;
    this.renderCharts$.next();
  }

  createChevronButton(direction, width) {
    const icon = this.renderer.createElement("i");
    this.renderer.addClass(icon, "glyphicon");
    this.renderer.addClass(
      icon,
      `icon-chevron-${direction ? "right" : "left"}`
    );

    const button = this.renderer.createElement("button");
    this.renderer.setAttribute(button, "type", "button");
    button.classList.add("btn", "btn-light");
    this.renderer.setStyle(button, "opacity", 0.5);
    this.renderer.setStyle(button, "pointer-events", "all");
    this.renderer.appendChild(button, icon);

    const foreignObject = this.renderer.createElement(
      "foreignObject",
      "http://www.w3.org/2000/svg"
    );
    // this.renderer.setAttribute(foreignObject, 'x', `${direction ? `${width*0.85}` : '0'}`);
    this.renderer.setAttribute(foreignObject, "y", "50");
    this.renderer.setAttribute(
      foreignObject,
      "width",
      `${direction ? `${width * 0.95}` : "50"}`
    );
    this.renderer.setAttribute(foreignObject, "height", "50");
    this.renderer.setStyle(foreignObject, "pointer-events", "none");
    if (!direction) {
      this.renderer.setAttribute(
        foreignObject,
        "x",
        `${direction ? `${width * 0.85}` : "0"}`
      );
    } else {
      this.renderer.setStyle(button, "float", "right");
    }

    this.renderer.appendChild(foreignObject, button);

    return foreignObject;
  }

  getSeasonEnd(fetchedData) {
    const seasonEnd: string[] =
      fetchedData["ffdate"][`fac${this.selectedDate.upper + 1}`].split("-");
    this.seasonEndDate = new NgbDate(
      parseInt(seasonEnd[0]),
      parseInt(seasonEnd[1]),
      parseInt(seasonEnd[2])
    );
    this.seasonEndWeek = this.parserService.getWeekNumber(this.seasonEndDate);
  }

  /**
   * Create objects holding information about frost free period.
   * Frost free period for selected year starts with last frost day (lfdate factor) representing date of last frost for season year-1 / year
   * Frost free period for selected year ends with first fall freeze for season year/ year + 1
   */
  getFrostFreeSeason(fetchedData) {
    [this.selectedDate.upper, this.selectedDate.upper + 1].forEach(
      (yearInSeason) => {
        this.frostFreePeriod[yearInSeason] = {};

        this.setFrostFreePeriodBound(fetchedData, yearInSeason, "ffdate");
        this.setFrostFreePeriodBound(fetchedData, yearInSeason, "lfdate");
      }
    );
  }

  private setFrostFreePeriodBound(fetchedData, year, factor) {
    const bound = factor == "ffdate" ? "end" : "start";
    this.frostFreePeriod[year][bound] = {};
    const firstFrostDate: string[] =
      fetchedData[factor][`fac${year}`].split("-");
    this.frostFreePeriod[year][bound]["date"] = new NgbDate(
      parseInt(firstFrostDate[0]),
      parseInt(firstFrostDate[1]),
      parseInt(firstFrostDate[2])
    );
    this.frostFreePeriod[year][bound]["week"] =
      this.parserService.getWeekNumber(
        this.frostFreePeriod[year][bound]["date"]
      );
  }

  isWithinFrostPeriod(date): void {
    const season = this.frostFreePeriod[date.year];
    const outsideOfBounds =
      date.week > season.start.week && date.week < season.end.week;
    //Rreversed logic for southern hemisphere ->  its freezing when within the bounds of season
    date["frost"] =
      season.start.week < season.end.week
        ? date.week < season.start.week || date.week > season.end.week
          ? this.HsLanguageService.getTranslationIgnoreNonExisting(
              "CHARTS",
              "frostPeriod"
            )
          : null
        : date.week < season.start.week && date.week > season.end.week
        ? this.HsLanguageService.getTranslationIgnoreNonExisting(
            "CHARTS",
            "frostPeriod"
          )
        : null;
  }

  makeClimaticDataRequest(url, factor, dataContainer): Observable<any> {
    return this.http
      .get(url, {
        observe: "response",
        responseType: "json",
      })
      .pipe(
        // timeout(1000),
        map((x: any) => {
          dataContainer[factor] = x.body.properties;
          return x.body;
        }),
        catchError((e) => {
          console.warn(e);
          return of(e);
        })
      );
  }

  isWithinDataBbox(coords): boolean {
    const extent = olExtent.boundingExtent(
      this.dataBbox.find((item) => item.location == this.selectedLocation).bbox
    );
    return olExtent.containsXY(extent, coords[0], coords[1]);
  }

  fetchAndRender(coords) {
    if (this.isWithinDataBbox(coords)) {
      this.ChartsService.generatingCharts = true;
      this.selectedCoords = coords;
      this.fetchData$.next(coords);

      this.awaitClimaData().then(() => {
        this.renderCharts$.next();

        setTimeout(() => {
          this.ChartsService.generatingCharts = false;
          this.climaDataLoading = false;
          this.baseChartsLoading = false;
        }, 500);
      });
    } else {
      this.selectedCoords = null;
      this.hsToastService.createToastPopupMessage(
        this.HsLanguageService.getTranslation(
          "STRATEGY.errorWhileRequestingChartData"
        ),
        this.HsLanguageService.getTranslationIgnoreNonExisting(
          "STRATEGY",
          "coordinatesOutOfBounds"
        )
      );
    }
  }

  async awaitClimaData(): Promise<any> {
    while (this.climaDataLoading || this.baseChartsLoading) {
      await new Promise((r) => setTimeout(r, 200));
    }
    return true;
  }

  handleFetchError(data) {
    this.selectedCoords = null;
    this.hsToastService.createToastPopupMessage(
      this.HsLanguageService.getTranslation(
        "STRATEGY.errorWhileRequestingChartData"
      ),
      this.HsLanguageService.getTranslationIgnoreNonExisting(
        "ERRORMESSAGES",
        data[0].status ? data[0].status.toString() : data[0].message,
        { url: this.apiUrl }
      )
    );
  }

  dataFrequencyChanged(frequency: string): void {
    if (this.dataFrequency == frequency) return;
    this.dataFrequency = frequency;
    this.fetchAndRender(this.selectedCoords);
  }

  overviewSelected(type: string) {
    if (this.overview == type) return;
    this.overview = type;
    this.fetchAndRender(this.selectedCoords);
  }

  parseSelectedDataPairs() {
    let selectedPairs = JSON.parse(JSON.stringify(this.selectedDataPairs));
    let pairsToPars = [];
    selectedPairs[this.overview][this.dataFrequency].forEach((pair) => {
      if (
        pair.data == "hsu" &&
        !["corn", "wheat"].some((crop) => crop == this.selectedCrop)
      )
        return;
      if (Array.isArray(pair.data) && !pair.layered) {
        //TODO ARRAY OF INDEXES when applied ,create and cache
        pair.data = pair.data[pair.index];
        pair.mark = pair.mark[pair.index];
        pair.unit = Array.isArray(pair.unit)
          ? pair.unit[pair.index]
          : pair.unit;
      }
      pairsToPars.push(pair);
    });
    selectedPairs[this.overview][this.dataFrequency] = pairsToPars;
    return selectedPairs;
  }
}
